/**
 * Inline images in the editor. While editing, an image lives in the document as a data: URL; on
 * send it becomes an embedded attachment referenced by `cid:` (see mail/outgoing.ts).
 *
 * Limits mirror the platform API (services/platform-api, modules/mailbox/inline-images.ts), so
 * the user gets the error while composing instead of on send. The server checks again.
 */

export const INLINE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const MAX_INLINE_IMAGE_BYTES = 4 * 1024 * 1024;

export type ImageRejection = 'unsupported' | 'tooLarge';

export interface ImageFile {
  name: string;
  src: string;
}

export function isSupportedImage(file: File): boolean {
  return (INLINE_IMAGE_TYPES as readonly string[]).includes(file.type);
}

/** Image files of a paste/drop (other files are ignored by the editor). */
export function imageFiles(list: FileList | null | undefined): File[] {
  return Array.from(list ?? []).filter((file) => file.type.startsWith('image/'));
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('read failed'));
    };
    reader.readAsDataURL(file);
  });
}

/** Reads accepted images; rejected ones are reported by name and reason. */
export async function readImages(files: readonly File[]): Promise<{ images: ImageFile[]; rejected: { name: string; reason: ImageRejection }[] }> {
  const images: ImageFile[] = [];
  const rejected: { name: string; reason: ImageRejection }[] = [];
  for (const file of files) {
    const name = file.name || 'image';
    if (!isSupportedImage(file)) rejected.push({ name, reason: 'unsupported' });
    else if (file.size > MAX_INLINE_IMAGE_BYTES) rejected.push({ name, reason: 'tooLarge' });
    else images.push({ name, src: await readAsDataUrl(file) });
  }
  return { images, rejected };
}
